import React, { useEffect, useReducer } from 'react';
import {
    IonButton,
    IonButtons,
    IonContent,
    IonPage,
    IonToolbar,
    useIonToast,
    IonSelect,
    IonSelectOption,
    IonItem,
    IonLabel,
    IonGrid,
    IonRow,
    IonTextarea,
    ToastOptions,
    IonFab,
    IonFabButton,
} from '@ionic/react';
import { defaultCatchOdooError, defaultToastOptions, goBackOrReplace, loadingAtom } from '../utils/Global';
import odoo, { saveOrCreateWithoutRead, standardizedOdooLeave, unstandardizedOdooLeave } from '../utils/odoo';
import { LeaveState, getMetaData, leaveInitialValue } from '../utils/odooUtils';
import DatePicker from '../components/DatePicker';
import { RoundedSquareIcon } from '../components/Icon';
import { faCloudMoon, faPencil, faPlane } from '@fortawesome/pro-regular-svg-icons';
import { useHistory, useParams } from 'react-router-dom';
import { useEmployees } from '../hooks/useSwr';
import { Header } from '../components/Header';
import { IosHeader } from '../components/IosHeader';
import { useAtom } from 'jotai';
import { Leave, leaveFields } from '../types/models';
import { each, toInteger } from 'lodash/fp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/pro-solid-svg-icons';
import { mutate } from 'swr';

const DetailLeave: React.FC = () => {
    const history = useHistory();

    const { id: rawLeaveId } = useParams<{ id?: string }>();
    const leaveId = toInteger(rawLeaveId);

    const [loading, setLoading] = useAtom(loadingAtom);

    const { data: employees } = useEmployees();

    const [presentToast] = useIonToast();

    const [leave, setLeave] = useReducer(
        (prev: Leave, next: Partial<Leave>) => {
            let leaveDisplayed = { ...prev, ...next };

            if (
                leaveDisplayed.mode == 'advanced' &&
                (!leaveDisplayed.start_half_day || !leaveDisplayed.end_half_day || !leaveDisplayed.date_end)
            ) {
                leaveDisplayed.start_half_day = 'morning';
                leaveDisplayed.end_half_day = 'morning';
                leaveDisplayed.date_end = leaveDisplayed.date_start;
            }

            if (next.mode == 'half_day' && !leaveDisplayed.half_day) {
                leaveDisplayed.half_day = 'morning';
            }

            return leaveDisplayed;
        },
        {
            ...leaveInitialValue(),
            ...{ employee_id: employees[0].id },
        }
    );
    const title = !leave.id
        ? 'Nouvelle demande de congés'
        : leave._access_right.write
        ? 'Modifier une demande de congés'
        : 'Détails de la demande de congés';

    const handleCreateLeave = async () => {
        try {
            await saveOrCreateWithoutRead(
                'hr.madper.app.leave',
                unstandardizedOdooLeave(leave, await getMetaData(false))
            );

            mutate((key) => Array.isArray(key) && key[0] === 'hr.madper.app.leave', undefined, { revalidate: true });

            presentToast({
                ...defaultToastOptions,
                message: 'La demande de congés a bien été créée.',
            });

            goBackOrReplace(history, '/mesconges');
        } catch (error: any) {
            // Handle errors
            if (error.messages) {
                each((errorToast: ToastOptions) => presentToast(errorToast), error.messages);
            } else {
                presentToast({
                    ...defaultToastOptions,
                    message: error.message,
                    color: 'danger',
                });
            }
        }
    };

    const unlinkLeave = (id: number) => async () => {
        try {
            await odoo.unlink('hr.madper.app.leave', id);

            mutate((key) => Array.isArray(key) && key[0] === 'hr.madper.app.leave', undefined, { revalidate: true });

            presentToast({
                ...defaultToastOptions,
                message: 'La demande de congés a bien été supprimée.',
            });

            goBackOrReplace(history, '/mesconges');
        } catch (error: any) {
            // Handle errors
            defaultCatchOdooError(error, presentToast);
        }
    };

    //TODO: use swr pour récupérer les congés
    // Récupération d'un congé pour pouvoir le modifier
    useEffect(() => {
        setLoading(true);

        const getOdooLeave = async () => {
            if (!leaveId) {
                return;
            }

            const employeeLeave = await odoo.searchReadOne(
                'hr.madper.app.leave',
                {
                    fields: leaveFields,
                    domain: [['id', '=', leaveId]],
                },
                true
            );

            if (employeeLeave) {
                const standardizedLeave = standardizedOdooLeave(employeeLeave);
                setLeave(standardizedLeave);
            }
        };

        getOdooLeave().finally(() => {
            setLoading(false);
        });
    }, [leaveId]);

    // Disable the Mode Selector to prevent field visibility issues when changing mode for existing records (bug to fix).
    // Additionally, disable the Mode Selector if the user lacks the necessary permissions.
    const disabledModeSelector = !leave._access_right.write || !!leaveId;

    if (loading) {
        return null;
    }

    return (
        <IonPage id="validation-page">
            <IosHeader title={title} backText="Retour" backLocation="/mesconges" />
            {leave._access_right.unlink && (
                <IonFab slot="fixed" vertical="bottom" horizontal="end">
                    <IonFabButton color={'danger'} onClick={unlinkLeave(leave.id)}>
                        <FontAwesomeIcon icon={faTrashCan} />
                    </IonFabButton>
                </IonFab>
            )}

            <IonContent>
                <Header title={title} backText={'Retour'} backLocation="/mesconges" />
                <div className="main-content-d">
                    {/* Sélecteur pour choisir le mode de saisie */}
                    <div className="xl:w-3/5 ">
                        <IonToolbar style={{ '--background': 'transparent' }}>
                            <div className="toolbar-leave flex">
                                <IonButtons>
                                    <IonButton
                                        onClick={() => setLeave({ mode: 'full_day' })}
                                        fill="solid"
                                        color={leave.mode === 'full_day' ? 'success' : 'medium'}
                                        disabled={disabledModeSelector}
                                    >
                                        Journée entière
                                    </IonButton>
                                </IonButtons>
                                <IonButtons>
                                    <IonButton
                                        onClick={() => setLeave({ mode: 'half_day' })}
                                        fill="solid"
                                        color={leave.mode === 'half_day' ? 'success' : 'medium'}
                                        disabled={disabledModeSelector}
                                    >
                                        Demi-journée
                                    </IonButton>
                                </IonButtons>
                                <IonButtons>
                                    <IonButton
                                        onClick={() => setLeave({ mode: 'advanced' })}
                                        fill="solid"
                                        color={leave.mode === 'advanced' ? 'success' : 'medium'}
                                        disabled={disabledModeSelector}
                                    >
                                        Avancé
                                    </IonButton>
                                </IonButtons>
                            </div>
                        </IonToolbar>
                        <div className="mt-5 content-leave">
                            <FormForLeaveDate
                                selectedMode={leave.mode}
                                updateLeaveData={setLeave}
                                leave={leave}
                                disabled={!leave._access_right.write}
                            />

                            {/* Sélecteur pour le type de congés */}

                            <IonItem
                                className="mb-5 mt-5 xl:rounded-md"
                                lines="full"
                                style={{ '--background': 'white' }}
                                disabled={!leave._access_right.write}
                            >
                                <RoundedSquareIcon icon={faPlane} />
                                <IonLabel className={`pl-1`}>Type de congés</IonLabel>

                                <IonSelect
                                    okText="OK"
                                    cancelText="Annuler"
                                    value={leave.type}
                                    placeholder="Type de congés"
                                    onIonChange={(e) => setLeave({ type: e.detail.value })}
                                    aria-label="Sélectionnez le type de congés"
                                    labelPlacement="end"
                                >
                                    <IonSelectOption value="paid_leave">Congés payés</IonSelectOption>
                                    <IonSelectOption value="unpaid_leave">Congés sans solde</IonSelectOption>
                                    <IonSelectOption value="rtt">RTT</IonSelectOption>
                                    <IonSelectOption value="other">Autre</IonSelectOption>
                                </IonSelect>
                            </IonItem>

                            <TetraItemTextArea
                                leave={leave}
                                updateLeaveData={setLeave}
                                label="Note employé"
                                val="employee_note"
                            />

                            {leave.adherent_note && (
                                <TetraItemTextArea
                                    leave={leave}
                                    updateLeaveData={setLeave}
                                    label="Note adhérent"
                                    val="adherent_note"
                                    disable={leave.state == LeaveState.WaitValidation}
                                />
                            )}

                            {leave.geiq_note && (
                                <TetraItemTextArea
                                    leave={leave}
                                    updateLeaveData={setLeave}
                                    label="Note Geiq"
                                    val="geiq_note"
                                    disable={leave.state == LeaveState.WaitValidation}
                                />
                            )}
                        </div>
                        {/* Bouton pour envoyer la demande */}
                        {(leave._access_right.write || leave.id === 0) && (
                            <div className="madper-button">
                                <IonButton
                                    shape="round"
                                    type="submit"
                                    expand="block"
                                    onClick={handleCreateLeave}
                                    className="btn"
                                >
                                    {leave.id ? 'Modifier' : 'Envoyer'} ma demande
                                </IonButton>
                            </div>
                        )}
                    </div>
                </div>
            </IonContent>
        </IonPage>
    );
};

const TetraItemTextArea: React.FC<{
    leave: Leave;
    updateLeaveData: (value: Partial<Leave>) => void;
    label: string;
    val: string;
    disable?: boolean;
}> = ({ leave, updateLeaveData, label, val, disable }) => {
    return (
        <IonItem
            lines="full"
            style={{ '--background': 'white' }}
            className="xl:rounded-md mb-5"
            disabled={disable || !leave._access_right.write}
        >
            <IonGrid className="pl-0">
                <IonRow>
                    <RoundedSquareIcon icon={faPencil} />
                    <IonLabel className="pl-1 leading-7">{label}</IonLabel>
                </IonRow>
                <IonRow>
                    <div className="w-full">
                        <IonTextarea
                            autoGrow={true}
                            aria-label={label}
                            placeholder="Tapez quelque chose ici"
                            value={leave[val as keyof Leave] as string}
                            onInput={(e: any) => {
                                updateLeaveData({ [val]: e.target.value });
                            }}
                        />
                    </div>
                </IonRow>
            </IonGrid>
        </IonItem>
    );
};

// Composant pour les dates du congé
const FormForLeaveDate: React.FC<{
    selectedMode: string;
    updateLeaveData: (value: Partial<Leave>) => void;
    leave: Leave;
    disabled: boolean;
}> = ({ selectedMode, updateLeaveData, leave, disabled }) => {
    // Pour arrondir le ion-item en bas ou en haut en fonction du mode
    let className_date_end = selectedMode === 'full_day' ? 'xl:rounded-b-md' : 'xl:rounded-t-md';

    return (
        <>
            <DatePicker
                className={'xl:rounded-t-md'}
                id={`confirm_input_time_start`}
                title={selectedMode === 'half_day' ? 'Date' : 'Date de début'}
                onChange={(date) => {
                    updateLeaveData({ date_start: date });
                    if (leave.date_end && date > leave.date_end) {
                        updateLeaveData({ date_end: date });
                    }
                }}
                defaultDate={leave.date_start}
                disabled={disabled}
            />

            {selectedMode === 'half_day' && (
                <IonItem
                    lines="full"
                    className="xl:rounded-b-md"
                    style={{ '--background': 'white' }}
                    disabled={disabled}
                >
                    <RoundedSquareIcon icon={faCloudMoon} />
                    <IonLabel className={`pl-1`}>Demi-journée</IonLabel>
                    <IonSelect
                        value={leave.half_day}
                        placeholder="Sélectionner le type"
                        onIonChange={(e) => updateLeaveData({ half_day: e.detail.value })}
                        aria-label="Sélectionnez la demi-journée"
                        labelPlacement="end"
                    >
                        <IonSelectOption value="morning">Matin</IonSelectOption>
                        <IonSelectOption value="afternoon">Après-midi</IonSelectOption>
                    </IonSelect>
                </IonItem>
            )}

            {selectedMode === 'advanced' && (
                <IonItem
                    lines="full"
                    className="xl:rounded-b-md mb-5"
                    style={{ '--background': 'white' }}
                    disabled={disabled}
                >
                    <RoundedSquareIcon icon={faCloudMoon} />
                    <IonLabel className={`pl-1`}>Période de début</IonLabel>
                    <IonSelect
                        value={leave.start_half_day}
                        placeholder="Sélectionner le moment"
                        onIonChange={(e) => updateLeaveData({ start_half_day: e.detail.value })}
                        aria-label="Sélectionnez la demi-journée de début"
                        labelPlacement="end"
                    >
                        <IonSelectOption value="morning">Matin</IonSelectOption>
                        <IonSelectOption value="afternoon">Après-midi</IonSelectOption>
                    </IonSelect>
                </IonItem>
            )}

            {(selectedMode === 'full_day' || selectedMode === 'advanced') && (
                <DatePicker
                    className={className_date_end}
                    id={`confirm_input_time_end`}
                    title="Date de fin"
                    onChange={(date) => {
                        const toUpdate = { date_end: date } as Partial<Leave>;
                        if (leave.date_start && leave.date_start > date) {
                            toUpdate.date_start = date;
                        }
                        updateLeaveData(toUpdate);
                    }}
                    defaultDate={leave.date_end}
                    disabled={disabled}
                />
            )}

            {selectedMode === 'advanced' && (
                <IonItem
                    lines="full"
                    className="xl:rounded-b-md"
                    style={{ '--background': 'white' }}
                    disabled={disabled}
                >
                    <RoundedSquareIcon icon={faCloudMoon} />
                    <IonLabel className={`pl-1`}>Période de fin</IonLabel>
                    <IonSelect
                        value={leave.end_half_day}
                        placeholder="Sélectionner le moment de fin"
                        onIonChange={(e) => updateLeaveData({ end_half_day: e.detail.value })}
                        aria-label="Sélectionnez la demi-journée de fin"
                        labelPlacement="end"
                    >
                        <IonSelectOption value="morning">Matin</IonSelectOption>
                        <IonSelectOption value="afternoon">Après-midi</IonSelectOption>
                    </IonSelect>
                </IonItem>
            )}
        </>
    );
};

export default DetailLeave;
